// Thin owner wrapper: calls the exhibit's F-F React Query mutation hooks and
// threads them (id-bound) into the shared <MediaPanelContainer> (review #8).
// Rendered by ExhibitForm in edit mode only. Snake_case wire fields per ADR-0020.

import type { FC } from "react";

import type { Exhibit, ExhibitUpdate } from "@shared/api/exhibits";
import {
  useGenerateExhibitNarration,
  useRemoveExhibitMedia,
  useSetExhibitNarration,
  useUpdateExhibit,
  useUpdateExhibitMedia,
  useUploadExhibitMedia,
  useUploadExhibitNarration,
} from "@shared/api/hooks/useExhibits";
import type { AudioLanguage } from "@shared/hooks/useTextToSpeech";

import MediaPanelContainer from "@staff/features/media/MediaPanelContainer";

// Legacy voice-URL field key by language (camelCase per ADR-0020).
const VOICE_URL_KEY: Record<AudioLanguage, keyof ExhibitUpdate> = {
  tc: "voiceUrlTc",
  en: "voiceUrlEn",
  sc: "voiceUrlSc",
};

export interface ExhibitMediaPanelProps {
  exhibit: Exhibit;
  disabled?: boolean;
  unsavedChanges?: boolean;
  descriptionChanged?: Partial<Record<AudioLanguage, boolean>>;
}

const ExhibitMediaPanel: FC<ExhibitMediaPanelProps> = ({
  exhibit,
  disabled,
  unsavedChanges,
  descriptionChanged,
}) => {
  const generate = useGenerateExhibitNarration();
  const uploadNarration = useUploadExhibitNarration();
  const setNarration = useSetExhibitNarration();
  const updateExhibit = useUpdateExhibit();
  const uploadMedia = useUploadExhibitMedia();
  const updateMedia = useUpdateExhibitMedia();
  const removeMedia = useRemoveExhibitMedia();

  const exhibitId = exhibit.id;

  return (
    <MediaPanelContainer
      ownerKey="exhibit"
      entity={exhibit}
      disabled={disabled}
      unsavedChanges={unsavedChanges}
      descriptionChanged={descriptionChanged}
      generatingLang={
        generate.isPending
          ? (generate.variables?.language as AudioLanguage)
          : null
      }
      uploadingMedia={uploadMedia.isPending}
      ops={{
        generate: (language) => generate.mutateAsync({ exhibitId, language }),
        uploadNarration: (language, file) =>
          uploadNarration.mutateAsync({ exhibitId, language, file }),
        setNarrationNull: (language) =>
          setNarration.mutateAsync({
            exhibitId,
            language,
            payload: { assetId: null },
          }),
        clearVoiceUrl: (language) =>
          updateExhibit.mutateAsync({
            exhibitId,
            payload: { [VOICE_URL_KEY[language]]: "" } as ExhibitUpdate,
          }),
        saveVoiceUrl: (language, url) =>
          updateExhibit.mutateAsync({
            exhibitId,
            payload: { [VOICE_URL_KEY[language]]: url } as ExhibitUpdate,
          }),
        uploadMedia: ({ file, kind, language, caption }) =>
          uploadMedia.mutateAsync({ exhibitId, file, kind, language, caption }),
        updateMedia: (assetId, patch) =>
          updateMedia.mutateAsync({ exhibitId, assetId, ...patch }),
        removeMedia: (assetId) =>
          removeMedia.mutateAsync({ exhibitId, assetId }),
      }}
    />
  );
};

export default ExhibitMediaPanel;
